'use client';

import { useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { BREADCRUMB_ITEMS } from '@/constants/constants';
import { BREADCRUMB_API_GUIDE_ITEMS } from '@/(routers)/(contentset)/hello-schoomy/api-guide/components/data/BlueBoardGuide';
import { BREADCRUMB_IDE_GUIDE_ITEMS } from '@/(routers)/(contentset)/hello-schoomy/ide-guide/components/data/IdeGuide';
import { BREADCRUMB_SCHOOMY_WORLD_GUIDE_ITEMS } from '@/(routers)/(contentset)/hello-schoomy/schoomy-world-guide/components/data/SchoomyWorldGuide';
import { BREADCRUMB_UNITY_GUIDE_ITEMS } from '@/(routers)/(contentset)/hello-schoomy/unity-guide/components/data/UnityGuide';
import { BREADCRUMB_CONNECTOR_GUIDE_ITEMS } from '@/(routers)/(contentset)/spot-live/connector-guide/components/data/ConnectorGuide';
import { BREADCRUMB_PROBLEM_SOLVING_ITEMS } from '@/(routers)/(contentset)/spot-live/problem-solving/components/data/ProblemSolving';

export type BreadcrumbItem = { label: string; href?: string };
type Opts = { leaf?: string };

const norm = (s: string) => s.replace(/\/+$/, ''); // 末尾スラッシュ無視

export function useBreadcrumb(opts?: Opts): BreadcrumbItem[] {
    const path = usePathname();
    const allBreadcrumbs = {
        ...BREADCRUMB_ITEMS,
        ...BREADCRUMB_UNITY_GUIDE_ITEMS,
        ...BREADCRUMB_API_GUIDE_ITEMS,
        ...BREADCRUMB_PROBLEM_SOLVING_ITEMS,
        ...BREADCRUMB_CONNECTOR_GUIDE_ITEMS,
        ...BREADCRUMB_IDE_GUIDE_ITEMS,
        ...BREADCRUMB_SCHOOMY_WORLD_GUIDE_ITEMS,
    };
    const leaf = opts?.leaf?.trim();

    return useMemo(() => {
        const p = norm(path);

        // 最長の「境界一致」キーを1件選ぶ（完全一致も含む）
        const hit = Object.entries(allBreadcrumbs)
            .filter(([key]) => {
                const k = norm(key);
                return p === k || p.startsWith(k + '/');
            })
            .sort((a, b) => {
                const da = a[0].split('/').length,
                    db = b[0].split('/').length;
                return db - da || b[0].length - a[0].length;
            })[0];

        const base = hit?.[1] ?? [];

        // 親が無い場合のフォールバック
        if (base.length === 0) {
            return leaf ? [{ label: 'ホーム', href: '/' }, { label: leaf }] : [{ label: 'ホーム', href: '/' }];
        }

        // leaf 未指定なら親だけ
        if (!leaf) return base;

        // 末尾と同名なら重複回避、そうでなければ1つ追加
        const last = base[base.length - 1]?.label;
        return last === leaf ? base : [...base, { label: leaf }];
    }, [path, leaf]);
}
